import { useEffect } from 'react';
import { useEventCallback } from 'usehooks-ts';

import { periodNames } from '@/features/subscription/constants';
import { useAvailablePackages } from '@/integrations/revenuecat/hooks/useAvailablePackages';
import { type Package, PackageType } from '@/integrations/revenuecat/types';
import {
  Choicebox,
  ChoiceboxItem,
  ChoiceboxItemContent,
  ChoiceboxItemDescription,
  ChoiceboxItemIndicator,
  ChoiceboxItemTitle,
} from '@/shared/components/ui/custom/choicebox';

import { SavingsBadge } from './savingsBadge';

export interface PlanChoiceboxProps {
  selectedPackageId: string | undefined;
  onSelectPackage: (packageToSelect: Package) => void;
  currentProductId?: string;
}

export function PlanChoicebox({
  selectedPackageId,
  onSelectPackage,
  currentProductId,
}: PlanChoiceboxProps) {
  const { data: packages } = useAvailablePackages();

  const hasWeeklySubscription = packages.some(
    pkg =>
      pkg.packageType === PackageType.WEEKLY &&
      pkg.product.identifier === currentProductId,
  );

  const displayedPackages = packages.filter(
    pkg => pkg.packageType !== PackageType.WEEKLY || hasWeeklySubscription,
  );

  const basePackageType = hasWeeklySubscription
    ? PackageType.WEEKLY
    : PackageType.MONTHLY;

  const baseProduct = packages.find(pkg => pkg.packageType === basePackageType);

  useEffect(() => {
    if (!selectedPackageId && currentProductId) {
      const currentPackage = displayedPackages.find(
        p => p.product.identifier === currentProductId,
      );
      if (currentPackage) {
        onSelectPackage(currentPackage);
      }
    }
  }, [selectedPackageId, currentProductId, displayedPackages, onSelectPackage]);

  const handleSelect = useEventCallback((packageId: string) => {
    const selectedPackage = packages.find(p => p.identifier === packageId);
    if (selectedPackage) {
      onSelectPackage(selectedPackage);
    }
  });

  return (
    <Choicebox
      value={selectedPackageId}
      onValueChange={handleSelect}
      className="w-full"
    >
      {displayedPackages.map(pkg => {
        const pricePerBasePeriod = hasWeeklySubscription
          ? pkg.product.pricePerWeekString
          : pkg.product.pricePerMonthString;
        const basePeriodString = hasWeeklySubscription ? 'week' : 'month';

        return (
          <ChoiceboxItem
            key={pkg.identifier}
            value={pkg.identifier}
            className="relative"
            data-current={
              pkg.product.identifier === currentProductId || undefined
            }
          >
            <ChoiceboxItemContent>
              <ChoiceboxItemTitle>
                {periodNames[pkg.packageType]}
                {pkg.product.identifier === currentProductId && ' (Current)'}
              </ChoiceboxItemTitle>
              <ChoiceboxItemDescription className="flex items-center gap-2">
                {pkg.product.priceString}
                {pkg.packageType !== basePackageType && pricePerBasePeriod && (
                  <span className="text-cold-400 font-normal">
                    {pricePerBasePeriod} / {basePeriodString}
                  </span>
                )}
              </ChoiceboxItemDescription>
            </ChoiceboxItemContent>
            {pkg.packageType !== basePackageType && baseProduct && (
              <SavingsBadge
                basePrice={
                  hasWeeklySubscription
                    ? baseProduct.product.pricePerWeek
                    : baseProduct.product.pricePerMonth
                }
                price={
                  hasWeeklySubscription
                    ? pkg.product.pricePerWeek
                    : pkg.product.pricePerMonth
                }
              />
            )}
            <ChoiceboxItemIndicator />
          </ChoiceboxItem>
        );
      })}
    </Choicebox>
  );
}
